import { useState, useMemo, useCallback } from 'react';
import { Search, MessageSquarePlus } from 'lucide-react';
import { useGetPublicArticlesQuery, Article } from '../../store/apis/helpCenter.api';
import { HelpCategories } from './HelpCategories';
import { HelpArticle } from './HelpArticle';
import { ArticleList } from './ArticleList';
import { HelpContactForm } from './HelpContactForm';
import { HelpArticleRow } from './HelpArticleRow';
import Modal2 from '../common/Modal2';
import { Button, DataTable, DataTableCard, TableSkeleton } from '../common';
import type { TableColumn } from '../common/Table';
import { CategoryGridSkeleton, SearchResultSkeleton } from './Skeleton';
import { HELP_STATIC_CATEGORIES, mapModuleToCategory, type HelpStaticKey } from '../../utils/helpCenter';

export function HelpCenterMain() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<HelpStaticKey | null>(null);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactFormProps, setContactFormProps] = useState<{
        moduleCode?: string;
        articleId?: string;
        isLocked?: boolean;
    }>({});

    const { data: publicArticlesData, isLoading: articlesLoading, isError: articlesError, refetch: refetchArticles } = useGetPublicArticlesQuery();

    // Flatten all articles from all modules into a single searchable array
    const allArticles = useMemo(() => {
        if (!publicArticlesData) return [];
        return Object.values(publicArticlesData).flat();
    }, [publicArticlesData]);

    // Perform local search filtering
    const searchResults = useMemo(() => {
        if (searchQuery.length < 2) return [];
        const query = searchQuery.toLowerCase();
        return allArticles.filter(article =>
            article.title.toLowerCase().includes(query) ||
            article.content.toLowerCase().includes(query)
        ).slice(0, 10);
    }, [allArticles, searchQuery]);

    // Static Figma cards with dynamic counts mapped from backend modules
    const categoryCounts = useMemo(() => {
        return HELP_STATIC_CATEGORIES.map((cat) => ({
            key: cat.key,
            articleCount: allArticles.filter((a) => mapModuleToCategory(a.moduleCode).key === cat.key).length,
        }));
    }, [allArticles]);

    const selectedCategory = selectedCategoryKey
        ? HELP_STATIC_CATEGORIES.find((c) => c.key === selectedCategoryKey) ?? null
        : null;

    const selectedCategoryArticles = useMemo(() => {
        if (!selectedCategoryKey) return [];
        return allArticles.filter((a) => mapModuleToCategory(a.moduleCode).key === selectedCategoryKey);
    }, [allArticles, selectedCategoryKey]);

    // Popular Articles — sorted by highest view count
    const popularArticles = useMemo(() => {
        return [...allArticles]
            .sort((a, b) => (b.views || 0) - (a.views || 0))
            .slice(0, 6);
    }, [allArticles]);

    const handleOpenArticle = useCallback((id: string) => {
        setSelectedArticleId(id);
    }, []);

    const handleCategoryClick = useCallback((key: HelpStaticKey) => {
        setSelectedCategoryKey((prev) => (prev === key ? null : key));
    }, []);

    const handleBack = () => {
        setSelectedArticleId(null);
        setSelectedCategoryKey(null);
        setShowContactForm(false);
        setContactFormProps({});
    };

    const handleArticleBack = () => {
        setSelectedArticleId(null);
    };

    // Popular Articles on Table2 with the exact Figma row view.
    // className "p-0" neutralises DataTable's own cell padding via
    // tailwind-merge so the Figma row padding (14px/22px) renders exact.
    const popularColumns = useMemo<TableColumn<Article>[]>(() => [
        {
            key: "article",
            title: "",
            label: "Article",
            required: true,
            className: "p-0",
            render: (_value, record) => (
                <HelpArticleRow
                    article={record}
                    categoryName={mapModuleToCategory(record.moduleCode).label}
                    onOpen={handleOpenArticle}
                />
            ),
        },
    ], [handleOpenArticle]);

    if (articlesError) {
        return (
            <div className="w-full">
                <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
                    <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load help center</h3>
                    <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
                        Something went wrong while fetching help articles. Please try again.
                    </p>
                    <Button appearance="primary" size="small" onClick={() => refetchArticles()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Figma hero: gradient banner + centered search + feedback pill */}
            <div className="relative overflow-hidden text-center px-6 py-10 mb-5 sm:mb-6 rounded-card border-0 bg-[linear-gradient(135deg,var(--color-primary-700),color-mix(in_srgb,var(--color-primary-700)_72%,transparent))]">
                <div className="absolute -top-10 -right-5 w-[180px] h-[180px] rounded-full bg-white/10 pointer-events-none" />
                <h1 className="relative text-heading-lg sm:text-heading-xl text-white">
                    How can we help?
                </h1>
                <p className="relative mt-2 mb-[22px] text-subtitle text-white/80">
                    Search articles or browse by category
                </p>
                <div className="relative max-w-[520px] mx-auto">
                    <div className="flex items-center gap-2.5 h-[50px] px-[18px] bg-[var(--color-bg-base)] rounded-lg shadow-[var(--shadow-lg)] transition-shadow focus-within:ring-2 focus-within:ring-white/70">
                        <Search className="w-[19px] h-[19px] text-[var(--color-text-tertiary)] flex-none" />
                        <input
                            placeholder="Search the help center…"
                            aria-label="Search the help center"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="border-0 bg-transparent outline-none text-body-md text-[var(--color-text-primary)] w-full placeholder:text-[var(--color-text-tertiary)]"
                        />
                    </div>

                    {/* Search Results Dropdown */}
                    {searchQuery.length >= 2 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-xl shadow-[var(--shadow-xl)] overflow-hidden z-50 text-left">
                            {articlesLoading ? (
                                <SearchResultSkeleton />
                            ) : searchResults && searchResults.length > 0 ? (
                                <div className="max-h-[400px] overflow-y-auto divide-y divide-[var(--color-border-tertiary)]">
                                    {searchResults.map((article: Article) => (
                                        <button
                                            key={article._id}
                                            type="button"
                                            onClick={() => {
                                                handleOpenArticle(article._id);
                                                setSearchQuery('');
                                            }}
                                            className="w-full p-5 text-left hover:bg-[var(--color-bg-muted)] transition-colors group focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
                                        >
                                            <span className="block text-label font-bold text-[var(--color-text-primary)] group-hover:text-[var(--color-primary-600)] transition-colors truncate">
                                                {article.title}
                                            </span>
                                            <span className="block text-body-xs text-[var(--color-text-secondary)] line-clamp-1 leading-relaxed mt-0.5">
                                                {article.content.replace(/[#*`]/g, '').substring(0, 150)}...
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-10 text-center">
                                    <p className="text-[var(--color-text-secondary)] font-medium text-body-sm">No results found for "{searchQuery}"</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="relative mt-4">
                    <button
                        type="button"
                        onClick={() => {
                            setContactFormProps({});
                            setShowContactForm(true);
                        }}
                        className="h-11 px-4 border border-white/40 bg-white/10 rounded-full text-white text-body-sm font-bold cursor-pointer inline-flex items-center gap-[7px] hover:bg-white/20 transition-colors focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
                    >
                        <MessageSquarePlus className="w-[15px] h-[15px]" />
                        Submit Feedback
                    </button>
                </div>
            </div>

            {/* Static category cards with dynamic counts */}
            <div className="mb-5 sm:mb-6">
                {articlesLoading ? (
                    <CategoryGridSkeleton />
                ) : (
                    <HelpCategories
                        categories={categoryCounts}
                        onCategoryClick={handleCategoryClick}
                    />
                )}
            </div>

            {/* Inline open-category panel (Figma: expands in place) */}
            {selectedCategory && (
                <div className="mb-5 sm:mb-6">
                    <ArticleList
                        title={selectedCategory.label}
                        articles={selectedCategoryArticles}
                        loading={articlesLoading}
                        categoryNameForRows={(a) => mapModuleToCategory(a.moduleCode).label}
                        onBack={handleBack}
                        onArticleClick={handleOpenArticle}
                    />
                </div>
            )}

            {/* Popular Articles on Table2 */}
            {!articlesLoading && popularArticles.length > 0 && (
                <DataTableCard title="Popular Articles" count={popularArticles.length}>
                    <DataTable
                        columns={popularColumns}
                        rows={popularArticles}
                        rowKey={(r) => r._id}
                        loading={articlesLoading}
                        skeleton={<TableSkeleton rows={6} columns={1} />}
                        emptyMessage="No popular articles yet"
                        responsive="scroll"
                    />
                </DataTableCard>
            )}

            {/* Article reader modal */}
            {selectedArticleId && (
                <HelpArticle
                    articleId={selectedArticleId}
                    onBack={handleArticleBack}
                />
            )}

            {/* Submit Feedback modal */}
            <Modal2
                isOpen={showContactForm}
                onClose={() => {
                    setShowContactForm(false);
                    setContactFormProps({});
                }}
                title="Submit Feedback"
                subtitle="Tell us how we can improve the Help Center"
                width={640}
            >
                <HelpContactForm
                    onBack={() => {
                        setShowContactForm(false);
                        setContactFormProps({});
                    }}
                    initialModuleCode={contactFormProps.moduleCode}
                    initialArticleId={contactFormProps.articleId}
                    isLocked={contactFormProps.isLocked}
                />
            </Modal2>
        </div>
    );
}

export default HelpCenterMain;
